import isBrowser from "src/utils/isBrowser";
import type {
  ModelContext,
  WebMcpToolDefinition,
  WebMcpToolOutcome,
  WebMcpToolResult,
} from "./types";

export type RegisterWebMcpToolsOptions = {
  /**
   * Called once per tool invocation. Our stand-in for the spec's
   * `toolactivated`/`toolcancel` events (not in the current imperative API) —
   * instrumenting `execute` is invocation-accurate.
   */
  onInvoke?: (toolName: string, outcome: WebMcpToolOutcome) => void;
};

export type WebMcpRegistration = {
  /** Stops the watcher and unregisters every tool. Safe to call anytime. */
  teardown: () => void;
};

const WATCH_INTERVAL_MS = 500;
const WATCH_TIMEOUT_MS = 120_000;

/**
 * Resolve the browser-provided `modelContext`. The spec moved this from
 * `navigator` to `document`; prefer the current location and fall back.
 */
const getModelContext = (): ModelContext | undefined => {
  if (!isBrowser()) {
    return undefined;
  }
  return document.modelContext ?? navigator.modelContext ?? undefined;
};

const toToolResult = (data: unknown): WebMcpToolResult => ({
  content: [
    {
      type: "text",
      text: typeof data === "string" ? data : JSON.stringify(data),
    },
  ],
});

/**
 * Register a set of read-only WebMCP tools for the current page.
 *
 * The WebMCP API is often injected *after* page load (by the browser/extension),
 * so we don't check once — we watch (poll + re-try on focus/visibility) until
 * `modelContext` appears, then register once. Serialises results, returns
 * descriptive errors so agents self-correct, and wires telemetry. The returned
 * `teardown` stops the watcher and unregisters the tools (call on unmount).
 *
 * No-ops on the server.
 */
export const registerWebMcpTools = (
  tools: WebMcpToolDefinition[],
  options: RegisterWebMcpToolsOptions = {},
): WebMcpRegistration => {
  if (!isBrowser()) {
    return { teardown: () => {} };
  }

  const { onInvoke } = options;
  let controller: AbortController | undefined;
  let registered = false;
  let interval: ReturnType<typeof setInterval> | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;

  const register = (modelContext: ModelContext) => {
    registered = true;
    controller = new AbortController();
    for (const tool of tools) {
      const execute = async (
        input: Record<string, unknown>,
      ): Promise<WebMcpToolResult> => {
        try {
          const data = await tool.execute(input);
          onInvoke?.(tool.name, "success");
          return toToolResult(data);
        } catch (error) {
          onInvoke?.(tool.name, "error");
          const message =
            error instanceof Error ? error.message : "Unknown error";
          return {
            content: [
              { type: "text", text: `Error in ${tool.name}: ${message}` },
            ],
            isError: true,
          };
        }
      };
      try {
        modelContext.registerTool(
          {
            name: tool.name,
            description: tool.description,
            inputSchema: tool.inputSchema ?? { type: "object", properties: {} },
            annotations: { readOnlyHint: tool.readOnly ?? true },
            execute,
          },
          { signal: controller.signal },
        );
      } catch (error) {
        // Pre-GA API shape may differ on older builds; fail safe and surface it
        // (forwarded to error reporting) rather than swallow silently.
        console.error(`WebMCP: failed to register tool "${tool.name}"`, error);
      }
    }
  };

  const stopWatching = () => {
    if (interval !== undefined) clearInterval(interval);
    if (timeout !== undefined) clearTimeout(timeout);
    interval = undefined;
    timeout = undefined;
    window.removeEventListener("focus", attemptRegistration);
    document.removeEventListener("visibilitychange", attemptRegistration);
  };

  function attemptRegistration() {
    if (registered) return;
    const modelContext = getModelContext();
    if (!modelContext) return;
    register(modelContext);
    stopWatching();
  }

  attemptRegistration();
  if (!registered) {
    interval = setInterval(attemptRegistration, WATCH_INTERVAL_MS);
    window.addEventListener("focus", attemptRegistration);
    document.addEventListener("visibilitychange", attemptRegistration);
    timeout = setTimeout(stopWatching, WATCH_TIMEOUT_MS);
  }

  return {
    teardown: () => {
      stopWatching();
      controller?.abort();
    },
  };
};
